import { styled } from '@mui/material';
import type { MouseEvent, ReactNode } from 'react';

import { LocationIcon, NextIcon, PhoneIcon } from '../../../icons';
import type { ResourceColorPalette } from '../../../theme/tokens/resourcesColors';
import { useCardResource } from '../CardResource';
import {
  PROVIDER_LIST_WIDE_QUERY,
  PROVIDER_TILE_SIZE,
  PROVIDER_TILE_SIZE_WIDE,
} from '../ProviderList/providerListContainer';

type Props = {
  name: string;
  href: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  /** Activity of the provider, e.g. "Maintenance ascenseur". */
  description?: string | null;
  address?: string | null;
  /** Telephone number for quick contact / emergency calls. */
  phone?: string | null;
  /** Tags or badges associated with the provider (e.g. specialty tags, status tag). */
  tags?: ReactNode;
  /** Secondary controls (resource menu). Rendered above the row link, never nested in it. */
  action?: ReactNode;
  'data-testid'?: string;
};

type OwnerState = {
  resourceColor: ResourceColorPalette;
};

type RowOwnerState = {
  hasDetails: boolean;
  hasTags: boolean;
};

function getMobileGridTemplateAreas(ownerState: RowOwnerState): string {
  if (ownerState.hasTags && ownerState.hasDetails) {
    return `
      "tile name trailing"
      "tile tags trailing"
      "tile details trailing"`;
  }
  if (ownerState.hasTags) {
    return `
      "tile name trailing"
      "tile tags trailing"`;
  }
  if (ownerState.hasDetails) {
    return `
      "tile name trailing"
      "tile details trailing"`;
  }
  return '"tile name trailing"';
}

const Row = styled('div')<{ ownerState: RowOwnerState }>(({ theme, ownerState }) => {
  const hasMultipleRows = ownerState.hasDetails || ownerState.hasTags;

  return {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: `${PROVIDER_TILE_SIZE}px minmax(0, 1fr) auto`,
    gridTemplateAreas: getMobileGridTemplateAreas(ownerState),
    columnGap: theme.spacing(1.5),
    rowGap: hasMultipleRows ? theme.spacing(0.5) : 0,
    alignItems: 'center',
    minHeight: hasMultipleRows ? theme.spacing(8.5) : theme.spacing(7),
    paddingBlock: theme.spacing(1.25),
    paddingInline: theme.spacing(2),
    transition: theme.transitions.create('background-color', {
      duration: theme.transitions.duration.shortest,
    }),
    '@media (hover: hover)': {
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    '&:has(a:focus-visible)': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
    [PROVIDER_LIST_WIDE_QUERY]: {
      gridTemplateColumns: `${PROVIDER_TILE_SIZE_WIDE}px minmax(0, 1fr) auto auto`,
      gridTemplateAreas: ownerState.hasDetails
        ? `
          "tile name tags trailing"
          "tile details details trailing"`
        : '"tile name tags trailing"',
      columnGap: theme.spacing(2),
      rowGap: ownerState.hasDetails ? theme.spacing(0.5) : 0,
      minHeight: ownerState.hasDetails ? theme.spacing(8) : theme.spacing(7),
      paddingBlock: theme.spacing(1.25),
    },
  };
});

const Tile = styled('span')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  gridArea: 'tile',
  alignSelf: 'center',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: PROVIDER_TILE_SIZE,
  height: PROVIDER_TILE_SIZE,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: ownerState.resourceColor.light,
  color: ownerState.resourceColor.main,
  [PROVIDER_LIST_WIDE_QUERY]: {
    width: PROVIDER_TILE_SIZE_WIDE,
    height: PROVIDER_TILE_SIZE_WIDE,
  },
}));

const NameLink = styled('a')(({ theme }) => ({
  ...theme.typography.body1Emphasis,
  gridArea: 'name',
  minWidth: 0,
  color: theme.palette.text.primary,
  textDecoration: 'none',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
  },
  '&:focus-visible': {
    outline: 'none',
  },
}));

const TagsSlot = styled('span')(({ theme }) => ({
  gridArea: 'tags',
  position: 'relative',
  zIndex: 1,
  display: 'inline-flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  alignItems: 'center',
  justifyContent: 'flex-start',
  justifySelf: 'start',
  minWidth: 0,
  [PROVIDER_LIST_WIDE_QUERY]: {
    justifyContent: 'flex-end',
    justifySelf: 'end',
  },
}));

const Details = styled('div')(({ theme }) => ({
  gridArea: 'details',
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  columnGap: theme.spacing(1),
  rowGap: theme.spacing(0.25),
}));

const Description = styled('span')(({ theme }) => ({
  ...theme.typography.body2,
  minWidth: 0,
  color: theme.palette.text.secondary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const Separator = styled('span')(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
  userSelect: 'none',
  flexShrink: 0,
}));

const Address = styled('span')(({ theme }) => ({
  ...theme.typography.body2,
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  minWidth: 0,
  color: theme.palette.text.secondary,
}));

const AddressText = styled('span')({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const PhoneLink = styled('a')(({ theme }) => ({
  ...theme.typography.body2,
  position: 'relative',
  zIndex: 1,
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  minWidth: 0,
  color: theme.palette.primary.main,
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const Trailing = styled('span')(({ theme }) => ({
  gridArea: 'trailing',
  alignSelf: 'center',
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.text.secondary,
}));

const ActionSlot = styled('span')(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  marginRight: theme.spacing(-1),
}));

/**
 * One provider of the space directory, inside a `ProviderList`.
 * The name is the row link (its hit area covers the whole row); the optional action
 * sits above it so the resource menu stays a separate control.
 */
export function ProviderListItem(props: Props) {
  const { resourceColor, IconComponent } = useCardResource({ variant: 'providers' });
  const hasDetails = Boolean(props.description || props.address || props.phone);
  const hasTags = Boolean(props.tags);

  return (
    <li data-testid={props['data-testid'] ?? 'provider-list-item'}>
      <Row ownerState={{ hasDetails, hasTags }}>
        <Tile aria-hidden="true" ownerState={{ resourceColor }}>
          {IconComponent ? <IconComponent fontSize="small" /> : null}
        </Tile>
        <NameLink href={props.href} onClick={props.onClick}>{props.name}</NameLink>
        {props.tags ? <TagsSlot>{props.tags}</TagsSlot> : null}
        {hasDetails ? (
          <Details>
            {props.description ? <Description>{props.description}</Description> : null}
            {props.description && props.address ? (
              <Separator aria-hidden="true">·</Separator>
            ) : null}
            {props.address ? (
              <Address>
                <LocationIcon fontSize="inherit" aria-hidden="true" />
                <AddressText>{props.address}</AddressText>
              </Address>
            ) : null}
            {props.phone ? (
              <>
                {(props.description || props.address) ? (
                  <Separator aria-hidden="true">·</Separator>
                ) : null}
                <PhoneLink
                  href={`tel:${props.phone}`}
                  onClick={(event) => event.stopPropagation()}
                  aria-label={`Appeler ${props.name}`}
                >
                  <PhoneIcon fontSize="inherit" aria-hidden="true" />
                  <span>{props.phone}</span>
                </PhoneLink>
              </>
            ) : null}
          </Details>
        ) : null}
        <Trailing>
          {props.action ? (
            <ActionSlot>{props.action}</ActionSlot>
          ) : (
            <NextIcon fontSize="small" aria-hidden="true" />
          )}
        </Trailing>
      </Row>
    </li>
  );
}

export type { Props as ProviderListItemProps };
